import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../lib/api.js';
import { useWorkspaceStore } from '../stores/workspace.js';

export interface SearchResult {
  id: string;
  type: 'company' | 'contact' | 'bucket' | 'order';
  title: string;
  subtitle: string | null;
  meta: string | null;
  url: string;
}

export interface SearchResults {
  companies: SearchResult[];
  contacts: SearchResult[];
  buckets: SearchResult[];
  orders: SearchResult[];
  query: string;
  total: number;
}

const EMPTY: SearchResults = {
  companies: [], contacts: [], buckets: [], orders: [], query: '', total: 0,
};

const DEBOUNCE_MS = 200;

export function useSearch(query: string) {
  const { getToken } = useAuth();
  const workspaceId = useWorkspaceStore((s) => s.workspace?.id);
  const [results, setResults] = useState<SearchResults>(EMPTY);
  const [isLoading, setIsLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    if (!query.trim() || !workspaceId) {
      setResults(EMPTY);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    timerRef.current = setTimeout(async () => {
      abortRef.current?.abort();
      abortRef.current = new AbortController();

      try {
        const token = await getToken();
        const data = await api.get<SearchResults>(
          `/v1/search?q=${encodeURIComponent(query)}&limit=5`,
          { token: token ?? undefined, workspaceId },
        );
        setResults(data);
      } catch {
        // Ignore abort errors
      } finally {
        setIsLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query, workspaceId]);

  return { results, isLoading };
}
